import { Box, Chip, CircularProgress, Collapse, Paper, Tooltip, Typography } from "@material-ui/core";
import { InfoOutlined, Warning } from "@material-ui/icons";
import * as React from "react";
import { CustomerScoreModel } from "../Model/AiReliabilityModels";

interface CustomerScoreCardProps {
  score: CustomerScoreModel | null;
  loading: boolean;
}

export const getRiskColor = (score: number): string => {
  if (score >= 80) return "#4caf50";
  if (score >= 60) return "#ff9800";
  return "#f44336";
};

export const getRiskLabel = (score: number): string => {
  if (score >= 80) return "Low Risk";
  if (score >= 60) return "Medium Risk";
  return "High Risk";
};

const SUMMARY_TRUNCATE_LENGTH = 150;

const CustomerScoreCard: React.FC<CustomerScoreCardProps> = ({ score, loading }) => {
  const [summaryExpanded, setSummaryExpanded] = React.useState(false);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!score) {
    return (
      <Paper variant="outlined" style={{ padding: 16, display: "flex", alignItems: "center", gap: 8, color: "#555" }}>
        <InfoOutlined style={{ color: "#2196f3" }} />
        <Typography variant="body2">Bu müşteri için henüz analiz yapılmamış.</Typography>
      </Paper>
    );
  }

  const riskColor = getRiskColor(score.overallScore);
  const isSummaryLong = score.aiSummary?.length > SUMMARY_TRUNCATE_LENGTH;
  const displayedSummary =
    isSummaryLong && !summaryExpanded ? score.aiSummary.slice(0, SUMMARY_TRUNCATE_LENGTH) + "..." : score.aiSummary;

  return (
    <Paper variant="outlined" style={{ padding: 20 }}>
      <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 24 }}>
        {/* Score Circle */}
        <Box display="flex" flexDirection="column" alignItems="center" minWidth={100}>
          <Box
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: `4px solid ${riskColor}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h5" style={{ color: riskColor, fontWeight: 700, lineHeight: 1 }}>
              {score.overallScore}
            </Typography>
            <Typography variant="caption" style={{ color: riskColor }}>
              / 100
            </Typography>
          </Box>
          <Chip
            label={getRiskLabel(score.overallScore)}
            size="small"
            style={{ marginTop: 8, backgroundColor: riskColor, color: "#fff", fontWeight: 600 }}
          />
        </Box>

        {/* Details */}
        <Box flex={1} minWidth={200}>
          <Box display="flex" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Tooltip title="Toplam kayıt sayısı">
              <Chip label={`${score.totalEntries} Kayıt`} size="small" variant="outlined" />
            </Tooltip>
            <Tooltip title="Yüksek riskli kayıt sayısı">
              <Chip
                label={`${score.highRiskEntryCount} Yüksek Risk`}
                size="small"
                icon={score.highRiskEntryCount > 0 ? <Warning style={{ fontSize: 14 }} /> : undefined}
                style={{
                  borderColor: score.highRiskEntryCount > 0 ? "#f44336" : undefined,
                  color: score.highRiskEntryCount > 0 ? "#f44336" : undefined,
                }}
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {/* AI Summary */}
          {score.aiSummary && (
            <Box mb={1}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                AI Özeti
              </Typography>
              <Typography variant="body2" style={{ marginTop: 2 }}>
                {displayedSummary}
              </Typography>
              {isSummaryLong && (
                <Typography
                  variant="caption"
                  style={{ cursor: "pointer", color: "#1976d2" }}
                  onClick={() => setSummaryExpanded(prev => !prev)}
                >
                  {summaryExpanded ? "Daha az gör" : "Daha fazla gör"}
                </Typography>
              )}
            </Box>
          )}

          {/* AI Recommendation */}
          {score.aiRecommendation && (
            <Box
              mt={1}
              p={1}
              style={{
                backgroundColor: "#fff8e1",
                borderLeft: "3px solid #ff9800",
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" style={{ fontWeight: 600, color: "#e65100" }}>
                Öneri
              </Typography>
              <Typography variant="body2" style={{ color: "#5d4037" }}>
                {score.aiRecommendation}
              </Typography>
            </Box>
          )}

          {/* Last Calculated */}
          {score.lastCalculatedAt && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
              Son güncelleme: {new Date(score.lastCalculatedAt).toLocaleString("tr-TR")}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default CustomerScoreCard;
